// Server-backed reviews CRUD. Stored in site_config under key="reviews".
// Public read returns admin overrides if present, otherwise compiled defaults.

import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { assertAdmin } from "@/lib/admin-guard";
import { z } from "zod";
import { reviews as defaultReviews, type ReviewConfig } from "@/lib/site-config";

function getAdminClient() {
  const url = process.env.SUPABASE_URL;
  const key = process.env.SUPABASE_SERVICE_ROLE_KEY;
  if (!url || !key) throw new Error("Server configuration error");
  return import("@supabase/supabase-js").then(({ createClient }) =>
    createClient(url, key, { auth: { persistSession: false, autoRefreshToken: false } })
  );
}

const reviewSchema = z.object({
  name: z.string().min(1).max(120),
  text: z.string().min(1).max(2000),
  rating: z.number().int().min(1).max(5),
  service: z.string().max(80),
  source: z.string().max(60).optional(),
  sourceUrl: z.string().url().max(500).optional().or(z.literal("").transform(() => undefined)),
  pages: z.array(z.string().min(1).max(60)).max(20).optional(),
});

export const getPublicReviews = createServerFn({ method: "GET" }).handler(
  async (): Promise<{ reviews: ReviewConfig[]; isCustom: boolean }> => {
    try {
      const supabase = await getAdminClient();
      // Prefer the new testimonials table.
      const { data: tRows } = await supabase
        .from("testimonials")
        .select("name,text,rating,service,source,source_url,pages")
        .eq("status", "published")
        .order("sort_order");
      if (Array.isArray(tRows) && tRows.length > 0) {
        const mapped: ReviewConfig[] = tRows.map((r) => ({
          name: r.name as string,
          text: r.text as string,
          rating: r.rating as number,
          service: (r.service as string) || "General",
          source: (r.source as string) || "Google",
          sourceUrl: (r.source_url as string) || undefined,
          pages: (r.pages as string[]) || ["all"],
        }));
        return { reviews: mapped, isCustom: true };
      }
      // Legacy fallback: site_config.reviews
      const { data } = await supabase
        .from("site_config")
        .select("value")
        .eq("key", "reviews")
        .maybeSingle();
      const arr = (data?.value as { items?: unknown })?.items;
      if (Array.isArray(arr) && arr.length > 0) {
        const items = arr
          .map((r) => { try { return reviewSchema.parse(r); } catch { return null; } })
          .filter((r): r is ReviewConfig => !!r);
        if (items.length > 0) return { reviews: items, isCustom: true };
      }
      return { reviews: defaultReviews, isCustom: false };
    } catch {
      return { reviews: defaultReviews, isCustom: false };
    }
  }
);

export const saveReviews = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((input: unknown) =>
    z.object({
      password: z.string().max(200).optional(),
      items: z.array(reviewSchema).max(80),
    }).parse(input)
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const supabase = await getAdminClient();
    const { error } = await supabase
      .from("site_config")
      .upsert({ key: "reviews", value: { items: data.items } as never, updated_at: new Date().toISOString() });
    if (error) throw new Error("Failed to save reviews");
    return { success: true, count: data.items.length };
  });
